import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Briefcase, Users, FileText, Sparkles, MessageSquare, Plus, ArrowUpRight, Crown, Globe2, Settings } from "lucide-react";
import SuggestedJobsWidget from "../components/SuggestedJobsWidget";
import FraudAlertBanner from "../components/FraudAlertBanner";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [profile, setProfile] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/profile/me").then((r) => setProfile(r.data.profile)).catch(() => {});
    if (user.role === "candidate") {
      api.get("/applications/mine").then((r) => setStats((s) => ({ ...s, applications: r.data.length }))).catch(() => {});
    } else {
      Promise.all([api.get("/jobs/mine"), api.get("/applications/mine")])
        .then(([jobs, apps]) => setStats({ jobs: jobs.data.length, applications: apps.data.length }))
        .catch(() => {});
    }
  }, [user]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/dashboard`,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || t("dashboard.portal_error");
      toast.error(msg);
      setPortalLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="mec-overline mb-2">{t("dashboard.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">
            {t("dashboard.hello")}, {user.full_name.split(" ")[0]} 👋
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            {user.role === "candidate" ? t("dashboard.candidate_sub") : t("dashboard.employer_sub")}
          </p>
        </div>
        {profile?.is_premium && (
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200" data-testid="premium-badge">
              <Crown className="w-3 h-3 mr-1" /> {user.role === "candidate" ? t("dashboard.premium_candidate") : t("dashboard.premium_employer")}
            </Badge>
            {profile?.stripe_subscription_id && profile?.premium_source !== "free_launch" && (
              <Button
                variant="outline"
                size="sm"
                onClick={openBillingPortal}
                disabled={portalLoading}
                className="border-slate-300 hover:bg-slate-50 text-slate-700"
                data-testid="manage-subscription-btn"
              >
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                {portalLoading ? t("dashboard.portal_loading") : t("dashboard.manage_subscription")}
              </Button>
            )}
          </div>
        )}
      </div>

      {user.role === "candidate" && <FraudAlertBanner />}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {user.role === "candidate" ? (
          <>
            <StatCard label={t("dashboard.stat_applications")} value={stats.applications ?? "—"} icon={<FileText className="w-5 h-5" />} testId="stat-applications" />
            <StatCard label={t("dashboard.stat_country")} value={user.country_code} icon={<Globe2 className="w-5 h-5" />} testId="stat-country" />
            <StatCard label={t("dashboard.stat_cv")} value={profile?.cv_path ? "✓" : "—"} icon={<FileText className="w-5 h-5" />} testId="stat-cv" />
          </>
        ) : (
          <>
            <StatCard label={t("dashboard.stat_jobs")} value={stats.jobs ?? "—"} icon={<Briefcase className="w-5 h-5" />} testId="stat-jobs" />
            <StatCard label={t("dashboard.stat_applications_received")} value={stats.applications ?? "—"} icon={<Users className="w-5 h-5" />} testId="stat-applications" />
            <StatCard label={t("dashboard.stat_status")} value={profile?.is_premium ? t("dashboard.premium") : t("dashboard.free")} icon={<Crown className="w-5 h-5" />} testId="stat-status" />
          </>
        )}
      </div>

      <div className="grid md:grid-cols-12 gap-4">
        {user.role === "candidate" ? (
          <>
            <ActionCard className="md:col-span-7" to="/ai-assistant" icon={<Sparkles />} title={t("dashboard.action_ai_title")} desc={t("dashboard.action_ai_desc")} accent testId="action-ai" />
            <ActionCard className="md:col-span-5" to="/profile" icon={<FileText />} title={t("dashboard.action_profile_title")} desc={t("dashboard.action_profile_desc")} testId="action-profile" />
            <ActionCard className="md:col-span-6" to="/jobs" icon={<Briefcase />} title={t("dashboard.action_jobs_title")} desc={t("dashboard.action_jobs_desc")} testId="action-jobs" />
            <ActionCard className="md:col-span-6" to="/messages" icon={<MessageSquare />} title={t("dashboard.action_messages_title")} desc={t("dashboard.action_messages_desc")} testId="action-messages" />
            {!profile?.is_premium && (
              <ActionCard className="md:col-span-12" to="/pricing" icon={<Crown />} title={t("dashboard.action_upgrade_candidate_title")} desc={t("dashboard.action_upgrade_candidate_desc")} accent testId="action-upgrade-candidate" />
            )}
          </>
        ) : (
          <>
            <ActionCard className="md:col-span-7" to="/jobs/new" icon={<Plus />} title={t("dashboard.action_new_job_title")} desc={t("dashboard.action_new_job_desc")} accent testId="action-new-job" />
            <ActionCard className="md:col-span-5" to="/jobs/mine" icon={<Briefcase />} title={t("dashboard.action_my_jobs_title")} desc={t("dashboard.action_my_jobs_desc")} testId="action-my-jobs" />
            <ActionCard className="md:col-span-6" to="/candidates" icon={<Users />} title={t("dashboard.action_candidates_title")} desc={t("dashboard.action_candidates_desc")} testId="action-candidates" />
            <ActionCard className="md:col-span-6" to="/profile" icon={<FileText />} title={t("dashboard.action_employer_profile_title")} desc={t("dashboard.action_employer_profile_desc")} testId="action-employer-profile" />
            {!profile?.is_premium && (
              <ActionCard className="md:col-span-12" to="/pricing" icon={<Crown />} title={t("dashboard.action_upgrade_title")} desc={t("dashboard.action_upgrade_desc")} accent testId="action-upgrade" />
            )}
          </>
        )}
      </div>

      {user.role === "candidate" && <SuggestedJobsWidget />}
    </div>
  );
}

function StatCard({ label, value, icon, testId }) {
  return (
    <div className="mec-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 text-slate-400">{icon}</div>
      <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function ActionCard({ className, to, icon, title, desc, accent, testId }) {
  return (
    <Link to={to} className={className} data-testid={testId}>
      <div className={`mec-card mec-card-hover p-6 h-full ${accent ? "border-blue-200 bg-blue-50/40" : ""}`}>
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent ? "bg-[#004EEB] text-white" : "bg-slate-100 text-slate-700"}`}>
            {React.cloneElement(icon, { className: "w-5 h-5" })}
          </div>
          <ArrowUpRight className="w-5 h-5 text-slate-400" />
        </div>
        <div className="font-display font-semibold text-slate-900 text-lg mb-1">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{desc}</div>
      </div>
    </Link>
  );
}
